import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor,
  useSensor, useSensors, useDraggable, useDroppable, pointerWithin,
} from '@dnd-kit/core';
import api from '../../services/api';
import DemandeNumeroOr from './DemandeNumeroOr';
import './KanbanAtelier.css';

// ═══════════════════════════════════════════════════════════════════════════
// Kanban Atelier — une colonne par statut, une carte par dossier.
//
// Écran TACTILE, pensé pour être lu à bout de bras et manipulé au doigt :
// glisser une carte change le statut, un appui ouvre le dossier.
//
// ⚠️ SUPER-ADMIN pour l'instant (garde côté menu ET côté rendu). Le geste écrit
// en base depuis l'atelier : on ouvre le périmètre quand l'écran aura été
// éprouvé, pas avant.
//
// ⚠️ AUCUNE nouvelle route serveur : les données viennent de
// `/planning/suivi-atelier` (mêmes dossiers, même borne de 500 par statut, même
// cloisonnement par concession) et l'écriture de
// `PATCH /planning/interventions/:id/statut-atelier`, qui journalise déjà le
// changement dans l'historique du dossier. Un Kanban n'est pas une donnée
// nouvelle, c'est une autre façon de regarder la même file.
//
// Le VOLUME est le vrai sujet : non filtré, l'écran affiche ~400 dossiers pour
// une seule concession, répartis entre 35 mécaniciens. Les filtres (atelier,
// mécanicien, réceptionnaire, ancienneté) ne sont pas un confort — c'est ce qui
// rend le tableau utilisable. Voir aussi le plafond `PLAFOND_COLONNE`.
// ═══════════════════════════════════════════════════════════════════════════

// Au-delà, une colonne devient un mur : on annonce le reste au lieu de l'empiler.
const PLAFOND_COLONNE = 60;
// Statuts de sortie : le travail atelier est fini, la voiture attend un règlement.
const STATUT_FACTURATION_RE = /(à facturer|facturé)\s*$/i;
// Demandé : « Restitué » n'a pas sa place sur le tableau de l'atelier.
const STATUT_EXCLU_RE = /restitué\s*$/i;

// Le « ~ » dit que la date est APPROCHÉE (`statut_atelier_since_estime`, reprise
// d'`updated_at` à la migration du 2026-08-14) — même convention que la modale,
// les notifications et la vue Tableaux. Sans lui, le tableau afficherait un mur
// de « 24 j » en rouge qui n'est pas une immobilisation mais une reprise.
const jourEtHeure = (iso, estime) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return (estime ? '~' : '')
    + d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// Pas de pastille d'ancienneté : elle a été retirée le 2026-09-03 parce qu'elle
// alertait sur tout. `statut_atelier_since` porte encore, pour des centaines de
// dossiers, l'horodatage de la MIGRATION du 09/08 (200+ lignes entre 01 h 16 et
// 01 h 18) — l'écran affichait donc « 25 j » en rouge partout, ce qui ne mesure
// aucune immobilisation. Une alerte qui s'allume toujours n'alerte plus.
// Les critères d'alerte viendront, mais métier : l'état des commandes de pièces
// pour les véhicules en attente de pièce, par exemple.

// ── Une carte ───────────────────────────────────────────────────────────────
// État de la commande de pièces (colonne « Att. pièces immo ») : le signal qui
// dit si le dossier peut avancer. Vert = pièces reçues → à reprogrammer.
// Source qui fait foi : l'état de la commande de pièces (table pieces_etat_or,
// exposé en `pieces_statut_livraison`) ; repli sur le champ hérité `statut_pieces`
// (Mécaplanning) quand aucune commande n'est suivie.
function etatPieces(d) {
  switch (d.pieces_statut_livraison) {
    case 'livre':              return { cls: 'ok',   label: 'Pièces reçues · à reprogrammer' };
    case 'recu_partiellement': return { cls: 'wait', label: 'Reçues partiellement' };
    case 'en_commande':        return { cls: 'wait', label: 'Pièces commandées' };
    default: break;
  }
  switch (d.statut_pieces) {
    case 'disponible':   return { cls: 'ok',   label: 'Pièces reçues · à reprogrammer' };
    case 'en_commande':  return { cls: 'wait', label: 'Pièces commandées' };
    case 'indisponible': return { cls: 'ko',   label: 'Back order' };
    default:             return { cls: 'none', label: 'Pièces à commander' };
  }
}
// Dossier « prêt à reprogrammer » = pièces livrées.
const piecesReprog = (d) => d.pieces_statut_livraison === 'livre' || d.statut_pieces === 'disponible';

// Signal colonne « À facturer » : le dossier est facturé si la dernière facture
// (factures_services, exposée en facture_date 'YYYY-MM-DD') est postérieure à son
// passage dans le statut. Rafraîchi ~1×/jour (export DataCar).
// `toutes` = mode rattrapage : on ignore la date de passage en statut (faussée au
// 11/08 par la bascule Mécaplanning) et on considère facturé dès qu'une facture
// existe, quelle que soit sa date.
const estFacturee = (d, toutes) => !!d.facture_date
  && (toutes || !d.statut_atelier_since || d.facture_date > String(d.statut_atelier_since).slice(0, 10));
function etatFacture(d, toutes) {
  if (estFacturee(d, toutes)) {
    const [, m, j] = d.facture_date.split('-');
    return { cls: 'ok', label: `Facturée le ${j}/${m}` };
  }
  return { cls: 'none', label: 'À facturer' };
}

// Signal colonne « Réceptionné » : la date de planification (la plus lointaine du
// dossier, exposée en date_planification) doit être aujourd'hui ou après. Passée
// (hier et avant), absente, ou dossier « à planifier » → à replanifier.
function ymdLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function etatPlanif(d) {
  if (!d.a_planifier && d.date_planification && d.date_planification >= ymdLocal()) {
    const [, m, j] = d.date_planification.split('-');
    return { cls: 'ok', label: `Planifié le ${j}/${m}` };
  }
  return { cls: 'ko', label: 'À replanifier' };
}

function Carte({ dossier, montrerMeca, onOuvrir, colonnePieces, colonneFacturer, colonneReception, factureToutes }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: String(dossier.id) });
  return (
    <div
      ref={setNodeRef}
      className={`kb-carte${isDragging ? ' is-dragging' : ''}`}
      onClick={() => onOuvrir(dossier)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(dossier); } }}
      /* Le nom peut être tronqué à l'écran : l'infobulle le rend en entier, avec
         l'OR et la plaque que la carte ne montre plus. */
      title={[dossier.client, dossier.numero_or && `OR ${dossier.numero_or}`, dossier.immatriculation]
        .filter(Boolean).join(' · ') + ' — ouvrir le dossier, glisser pour changer de statut'}
      {...listeners}
      {...attributes}
    >
      {/* Le CLIENT d'abord, en gras (demande utilisateur du 2026-09-03) : c'est
          par lui qu'on se repère, l'OR ne se retient pas. Sans nom, la plaque
          prend la place plutôt qu'une carte anonyme. */}
      <span className="kb-client">{dossier.client || dossier.immatriculation || 'Client inconnu'}</span>
      {/* L'OR et le mécanicien partagent une ligne : les empiler rendrait la
          carte plus haute qu'informative. Un dossier SANS OR (rendez-vous pris
          sans ouverture d'ordre) montre ici sa plaque — seul cas où
          l'immatriculation reste à l'écran. */}
      <span className="kb-ligne2">
        <span className="kb-or">{dossier.numero_or || dossier.immatriculation || '—'}</span>
        {montrerMeca && <span className="kb-meca">{dossier.mecanicien || 'Sans mécanicien'}</span>}
      </span>
      <span className="kb-pied">
        <span className="kb-quand">{jourEtHeure(dossier.statut_atelier_since, dossier.statut_atelier_since_estime) || '—'}</span>
      </span>
      {/* Bandeau d'état de la commande de pièces — le signal qui déclenche la
          reprogrammation. Uniquement sur la colonne « Att. pièces immo ». */}
      {colonnePieces && (() => {
        const e = etatPieces(dossier);
        return (
          <span className={`kb-pieces kb-pieces--${e.cls}`}>
            <span className="kb-dot" aria-hidden="true" />{e.label}
          </span>
        );
      })()}
      {colonneFacturer && (() => {
        const e = etatFacture(dossier, factureToutes);
        return (
          <span className={`kb-pieces kb-pieces--${e.cls}`}>
            <span className="kb-dot" aria-hidden="true" />{e.label}
          </span>
        );
      })()}
      {colonneReception && (() => {
        const e = etatPlanif(dossier);
        return (
          <span className={`kb-pieces kb-pieces--${e.cls}`}>
            <span className="kb-dot" aria-hidden="true" />{e.label}
          </span>
        );
      })()}
      {/* Mode rattrapage : la date de dernière facture s'affiche sur TOUTES les
          cartes de toutes les colonnes (sauf « À facturer » qui la porte déjà
          via son signal dédié), pour repérer d'un coup d'œil ce qui est facturé
          quel que soit le statut. */}
      {factureToutes && !colonneFacturer && dossier.facture_date && (() => {
        const [, m, j] = dossier.facture_date.split('-');
        return (
          <span className="kb-pieces kb-pieces--ok">
            <span className="kb-dot" aria-hidden="true" />Facturée le {j}/{m}
          </span>
        );
      })()}
    </div>
  );
}

// ── Zone de dépôt « Restituer » (dans le pupitre, toujours visible) ──────────
// Cible de sortie : on y glisse une carte pour passer le dossier à « Restitué ».
// Comme ce statut est exclu des colonnes, la carte disparaît d'elle-même du
// tableau. `arme` (un glisser est en cours) l'illumine pour inviter au dépôt.
function ZoneRestituer({ arme }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'restituer' });
  return (
    <div
      ref={setNodeRef}
      className={`kb-restituer${arme ? ' is-arme' : ''}${isOver ? ' is-over' : ''}`}
      aria-label="Glisser une carte ici pour restituer le véhicule"
    >
      <span className="kb-restituer-ico" aria-hidden="true">🔑</span>
      <span className="kb-restituer-txt">{isOver ? 'Relâcher pour restituer' : 'Restituer'}</span>
    </div>
  );
}

// ── Une colonne ─────────────────────────────────────────────────────────────
function Colonne({ statut, dossiers, montrerMeca, onOuvrir, enDeplacement, filtre, factureToutes }) {
  const { setNodeRef, isOver } = useDroppable({ id: `statut-${statut.id}` });
  // Une colonne vide se replie en rail — SAUF pendant un déplacement : c'est
  // précisément là qu'elle doit devenir une cible de dépôt atteignable au doigt.
  const repliee = dossiers.length === 0 && !enDeplacement;
  // Le compte serveur (`statut.count`) est brut (1 ligne/jour) ; après dédup par
  // OR côté client, il ne correspond plus aux cartes affichées. On affiche donc
  // le nombre de dossiers réellement montrés, et on neutralise le message de
  // troncature serveur (devenu incohérent avec la dédup).
  const tronquee = false;
  // Colonne « Att. pièces immo » : on montre l'état des commandes + combien sont
  // à reprogrammer (pièces reçues).
  const estPieces = /pi[eè]ce/i.test(statut.nom || '');
  const aReprog = estPieces ? dossiers.filter(piecesReprog).length : 0;
  // « À facturer » (pas « Facturé » : /facturer/ ne matche que le verbe).
  const estFacturer = /facturer/i.test(statut.nom || '');
  // Signal planif (« à replanifier » / « Planifié le »). Deux colonnes le portent :
  //  - « Réceptionné » — ancré au début pour ne PAS matcher « Non réceptionné ».
  //  - « Dossier préparé » — « préparé/ée/és » en fin (exclut « En préparation »).
  const estReception = /^r[eé]ceptionn/i.test((statut.nom || '').trim())
    || /pr[eé]par[eé]e?s?\s*$/i.test((statut.nom || '').trim());
  // Tri par tête de colonne selon le signal actionnable :
  //  - « À facturer » : les dossiers déjà facturés remontent en tête ;
  //  - « Réceptionné » / « Dossier préparé » : les « à replanifier » remontent
  //    en tête (le rouge = l'action à mener).
  // Tri stable → l'ordre d'origine est préservé au sein de chaque groupe.
  const dossiersAffiches = estFacturer
    ? [...dossiers].sort((a, b) => {
        const fa = estFacturee(a, factureToutes), fb = estFacturee(b, factureToutes);
        return fa === fb ? 0 : fa ? -1 : 1;
      })
    : estReception
      ? [...dossiers].sort((a, b) => {
          const ra = etatPlanif(a).cls === 'ko', rb = etatPlanif(b).cls === 'ko';
          return ra === rb ? 0 : ra ? -1 : 1;
        })
      : dossiers;
  return (
    <div ref={setNodeRef} className={`kb-colonne${repliee ? ' kb-vide' : ''}${isOver ? ' kb-survol' : ''}`}>
      <div className="kb-tete" style={{ background: statut.couleur, color: statut.text_color }}>
        <span className="kb-tete-nom">{statut.nom}</span>
        {/* Sans filtre, le vrai effectif est celui du serveur (`count`), pas le
            nombre de cartes chargées : la route borne chaque statut à 500. Le
            « + » dit la troncature au lieu de la taire — même règle que la vue
            Tableaux, où afficher « 500 » pour 1 074 dossiers serait un mensonge. */}
        {!repliee && (
          <span className="kb-tete-nb">
            {dossiers.length}
          </span>
        )}
      </div>
      {!repliee && estPieces && aReprog > 0 && (
        <div className="kb-tete-sub"><span className="kb-dot kb-dot--ok" aria-hidden="true" />{aReprog} à reprogrammer</div>
      )}
      {!repliee && (
        <div className="kb-pile">
          {/* onOuvrir reçoit la liste visible de la colonne : c'est ce qui
              alimente les flèches précédent/suivant dans la modale. */}
          {dossiersAffiches.slice(0, PLAFOND_COLONNE).map((d, _i, arr) => (
            <Carte key={d.id} dossier={d} montrerMeca={montrerMeca} onOuvrir={(dd) => onOuvrir(dd, arr)} colonnePieces={estPieces} colonneFacturer={estFacturer} colonneReception={estReception} factureToutes={factureToutes} />
          ))}
          {(dossiers.length > PLAFOND_COLONNE || tronquee) && (
            <div className="kb-trop">
              {dossiers.length > PLAFOND_COLONNE && <>+ {dossiers.length - PLAFOND_COLONNE} autres non affichés</>}
              {tronquee && <> · le serveur en charge {dossiers.length} sur {statut.count}</>}
              <br />Filtrez pour voir les vôtres.
            </div>
          )}
          {dossiers.length === 0 && <div className="kb-deposer">Déposer ici</div>}
        </div>
      )}
    </div>
  );
}

export default function KanbanAtelier({ staffList = [], ateliersList = [], onOpenIntervention }) {
  const [statuts, setStatuts] = useState([]);
  // « Restitué » est exclu des colonnes (STATUT_EXCLU_RE) mais on garde son objet
  // à part : c'est la cible de la zone de dépôt « Restituer » en haut à droite.
  const [statutRestitue, setStatutRestitue] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState(null);
  const [actif, setActif] = useState(null);            // dossier en cours de déplacement
  const [demandeOr, setDemandeOr] = useState(null);   // dossier sans N° d'OR après un dépôt

  const [fAtelier, setFAtelier] = useState('');
  const [fMeca, setFMeca] = useState('');
  const [fRecept, setFRecept] = useState('');
  const [fMois, setFMois] = useState('');       // 'AAAA-MM', ou 'sans' pour les dossiers sans date
  const [masquerFactu, setMasquerFactu] = useState(true);
  // Mode rattrapage : dans « À facturer », afficher la dernière facture de chaque
  // dossier quelle que soit sa date (contourne le statut_atelier_since faussé au
  // 11/08 par la bascule Mécaplanning). Masqué par défaut.
  const [factureToutes, setFactureToutes] = useState(false);

  const minuteur = useRef(null);
  useEffect(() => () => clearTimeout(minuteur.current), []);
  const annoncer = (texte, type = 'ok') => {
    setMessage({ texte, type });
    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => setMessage(null), 3500);
  };

  const charger = useCallback(() => {
    setChargement(true);
    api.get('/planning/suivi-atelier')
      .then(r => {
        const tous = r.data?.statuts || [];
        setStatuts(tous.filter(s => !STATUT_EXCLU_RE.test(s.nom)));
        setStatutRestitue(tous.find(s => STATUT_EXCLU_RE.test(s.nom)) || null);
        setDossiers(r.data?.dossiers || []);
        setErreur('');
      })
      .catch(() => setErreur('Impossible de charger le tableau.'))
      .finally(() => setChargement(false));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  // Un appui ne doit PAS être avalé par le glisser : la contrainte de distance
  // (souris) et le délai (doigt) laissent passer le clic d'ouverture du dossier.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  // ── Listes de filtres, tirées du personnel de la concession affichée ──────
  const mecaniciens = useMemo(
    () => staffList.filter(s => s.role === 'mecanicien')
      .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr')),
    [staffList]);
  const receptionnaires = useMemo(
    () => staffList.filter(s => s.role === 'receptionnaire')
      .sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr')),
    [staffList]);
  // L'atelier d'un dossier est celui de SON mécanicien : le dossier ne porte pas
  // d'atelier en propre.
  const atelierDeMeca = useMemo(() => {
    const m = new Map();
    for (const s of staffList) m.set(s.id, s.atelier_id || null);
    return m;
  }, [staffList]);

  // Les mois offerts sont ceux qui existent RÉELLEMENT dans les dossiers chargés :
  // proposer « juin » alors qu'aucun véhicule n'y a été reçu ferait cliquer dans
  // le vide. `date_reception` arrive en 'AAAA-MM-JJ' (TO_CHAR côté serveur), donc
  // un simple découpage suffit — pas de conversion, pas de décalage de fuseau.
  const moisDisponibles = useMemo(() => {
    const vus = new Set();
    let sansDate = 0;
    for (const d of dossiers) {
      const m = (d.date_reception || '').slice(0, 7);
      if (m) vus.add(m); else sansDate++;
    }
    return {
      liste: [...vus].sort().reverse(),
      sansDate,
    };
  }, [dossiers]);

  const libelleMois = (m) => {
    const [a, mo] = m.split('-');
    return new Date(Number(a), Number(mo) - 1, 1)
      .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const colonnes = useMemo(
    () => (masquerFactu ? statuts.filter(s => !STATUT_FACTURATION_RE.test(s.nom)) : statuts),
    [statuts, masquerFactu]);

  const visibles = useMemo(() => {
    const idsColonnes = new Set(colonnes.map(s => s.id));
    return dossiers.filter(d => {
      if (!idsColonnes.has(d.statut_atelier_id)) return false;
      if (fMeca && String(d.mecanicien_id || '') !== fMeca) return false;
      if (fRecept && String(d.poste_reception || '') !== fRecept) return false;
      if (fAtelier && String(atelierDeMeca.get(d.mecanicien_id) || '') !== fAtelier) return false;
      if (fMois) {
        const m = (d.date_reception || '').slice(0, 7);
        if (fMois === 'sans' ? m !== '' : m !== fMois) return false;
      }
      return true;
    });
  }, [dossiers, colonnes, fMeca, fRecept, fAtelier, fMois, atelierDeMeca]);

  const parStatut = useMemo(() => {
    // Dédup par n° d'OR au sein d'une colonne : un dossier immobilisé plusieurs
    // jours porte UNE ligne par jour (import Mécaplanning) mais ne doit apparaître
    // qu'UNE fois dans le Kanban (tableau de suivi de dossiers, pas d'agenda).
    // Représentant = le jour le plus récent ; `_nbJours` compte les jours.
    // Les dossiers SANS OR (RDV sans ordre ouvert) restent individuels.
    const cols = new Map();
    for (const d of visibles) {
      if (!cols.has(d.statut_atelier_id)) cols.set(d.statut_atelier_id, { parOr: new Map(), sansOr: [] });
      const col = cols.get(d.statut_atelier_id);
      const or = (d.numero_or || '').trim();
      if (!or) { col.sansOr.push(d); continue; }
      const ex = col.parOr.get(or);
      if (!ex) { col.parOr.set(or, { ...d, _nbJours: 1 }); continue; }
      const n = ex._nbJours + 1;
      if ((d.date_intervention || '') > (ex.date_intervention || '')) col.parOr.set(or, { ...d, _nbJours: n });
      else ex._nbJours = n;
    }
    const m = new Map();
    for (const [statutId, col] of cols) m.set(statutId, [...col.parOr.values(), ...col.sansOr]);
    return m;
  }, [visibles]);

  const surDebut = (e) => setActif(dossiers.find(d => String(d.id) === String(e.active.id)) || null);

  const surFin = async ({ active, over }) => {
    setActif(null);
    if (!over) return;
    const dossier = dossiers.find(d => String(d.id) === String(active.id));
    if (!dossier) return;
    // Zone « Restituer » (haut à droite) vs colonne de statut classique.
    const versRestitue = over.id === 'restituer';
    if (versRestitue && !statutRestitue) { annoncer('Statut « Restitué » introuvable.', 'erreur'); return; }
    const cible = versRestitue ? statutRestitue.id : Number(String(over.id).replace('statut-', ''));
    if (!cible || dossier.statut_atelier_id === cible) return;
    const statut = versRestitue ? statutRestitue : statuts.find(s => s.id === cible);
    const avant = dossier.statut_atelier_id;

    // Optimiste : la carte suit le doigt sans attendre le serveur. En cas
    // d'échec elle revient à sa colonne — l'écran ne doit jamais montrer un
    // statut que la base n'a pas enregistré.
    const appliquer = (statutId, s) => setDossiers(prev => prev.map(d => (d.id !== dossier.id ? d : {
      ...d,
      statut_atelier_id: statutId,
      statut_atelier_nom: s?.nom || null,
      statut_atelier_couleur: s?.couleur || null,
      statut_atelier_text_color: s?.text_color || null,
      statut_atelier_since: statutId === avant ? d.statut_atelier_since : new Date().toISOString(),
      statut_atelier_since_estime: false,
    })));
    appliquer(cible, statut);
    try {
      await api.patch(`/planning/interventions/${dossier.id}/statut-atelier`, { statut_atelier_id: cible });
      const nom = dossier.client || dossier.numero_or || dossier.immatriculation;
      annoncer(versRestitue ? `${nom} restitué` : `${nom} → ${statut?.nom}`);
      // Même règle que le menu contextuel du planning : un statut posé sur un
      // dossier sans N° d'OR le réclame aussitôt. Pas sur la restitution (sortie).
      if (!versRestitue && !dossier.numero_or) setDemandeOr(dossier);
    } catch (err) {
      appliquer(avant, statuts.find(s => s.id === avant));
      annoncer(err.response?.data?.error || 'Le changement de statut a échoué.', 'erreur');
    }
  };

  const raz = () => { setFAtelier(''); setFMeca(''); setFRecept(''); setFMois(''); setMasquerFactu(false); };
  const nbFiltres = (fAtelier ? 1 : 0) + (fMeca ? 1 : 0) + (fRecept ? 1 : 0) + (fMois ? 1 : 0);
  // Sans filtre, le total honnête est celui du serveur, pas celui des cartes
  // rapatriées (500 par statut au plus).
  const total = nbFiltres > 0
    ? visibles.length
    : colonnes.reduce((n, s) => n + (s.count ?? (parStatut.get(s.id) || []).length), 0);

  if (chargement) return <div className="kb-etat">Chargement du tableau…</div>;
  if (erreur) return <div className="kb-etat kb-etat-erreur">{erreur}</div>;

  return (
    <div className="kb-page">
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={surDebut} onDragEnd={surFin} onDragCancel={() => setActif(null)}>
      <div className="kb-pupitre">
        <div className="kb-champ">
          <label htmlFor="kb-atelier">Atelier</label>
          <select id="kb-atelier" className={fAtelier ? 'kb-actif' : ''} value={fAtelier} onChange={e => setFAtelier(e.target.value)}>
            <option value="">Tous les ateliers</option>
            {ateliersList.map(a => <option key={a.id} value={String(a.id)}>{a.nom || `Atelier ${a.id}`}</option>)}
          </select>
        </div>
        <div className="kb-champ">
          <label htmlFor="kb-meca">Mécanicien</label>
          <select id="kb-meca" className={fMeca ? 'kb-actif' : ''} value={fMeca} onChange={e => setFMeca(e.target.value)}>
            <option value="">Tous les mécaniciens</option>
            {mecaniciens.map(m => <option key={m.id} value={String(m.id)}>{m.nom} {m.prenom}</option>)}
          </select>
        </div>
        <div className="kb-champ">
          <label htmlFor="kb-recept">Réceptionnaire</label>
          <select id="kb-recept" className={fRecept ? 'kb-actif' : ''} value={fRecept} onChange={e => setFRecept(e.target.value)}>
            <option value="">Tous les réceptionnaires</option>
            {receptionnaires.map(r => <option key={r.id} value={String(r.id)}>{r.nom} {r.prenom}</option>)}
          </select>
        </div>
        <div className="kb-champ">
          <label htmlFor="kb-mois">Reçu en</label>
          <select id="kb-mois" className={fMois ? 'kb-actif' : ''} value={fMois} onChange={e => setFMois(e.target.value)}>
            <option value="">Tous les mois</option>
            {moisDisponibles.liste.map(m => <option key={m} value={m}>{libelleMois(m)}</option>)}
            {/* Rendu SEULEMENT s'il en existe : c'est un trou de données réel
                (l'import Mécaplanning n'écrivait pas `date_reception` avant le
                2026-09-02), et le cacher rendrait les totaux par mois faux sans
                le dire. */}
            {moisDisponibles.sansDate > 0 && (
              <option value="sans">Sans date de réception ({moisDisponibles.sansDate})</option>
            )}
          </select>
        </div>
        <div className="kb-champ">
          <label>Affichage</label>
          <div className="kb-jetons">
            <button type="button" className="kb-jeton" aria-pressed={masquerFactu} onClick={() => setMasquerFactu(v => !v)}>
              Masquer la facturation
            </button>
            <button type="button" className="kb-jeton" aria-pressed={factureToutes} onClick={() => setFactureToutes(v => !v)}
              title="Rattrapage : dans « À facturer », affiche la dernière facture de chaque dossier même émise avant la bascule Mécaplanning du 11/08 (ignore la date de changement de statut).">
              Rattrapage facturation
            </button>
          </div>
        </div>
        {(nbFiltres > 0 || masquerFactu) && (
          <button type="button" className="kb-raz" onClick={raz}>Tout réafficher</button>
        )}
        {statutRestitue && <ZoneRestituer arme={!!actif} />}
      </div>

      <div className="kb-compteur">
        <strong>{total.toLocaleString('fr-FR')}</strong> véhicule{total > 1 ? 's' : ''} {nbFiltres > 0 ? 'affiché' : 'dans ces statuts'}{nbFiltres > 0 && total > 1 ? 's' : ''}
        <span className="kb-doux">· {colonnes.length} colonne{colonnes.length > 1 ? 's' : ''}</span>
        {masquerFactu && <span className="kb-doux">· facturation masquée</span>}
        {factureToutes && <span className="kb-doux">· rattrapage facturation</span>}
        {message && <span className={`kb-message${message.type === 'erreur' ? ' kb-message-erreur' : ''}`}>{message.texte}</span>}
      </div>

      {demandeOr && (
        <DemandeNumeroOr
          dossier={demandeOr}
          onFerme={() => setDemandeOr(null)}
          onEnregistre={(num) => {
            setDossiers(prev => prev.map(d => (d.id === demandeOr.id ? { ...d, numero_or: num } : d)));
            setDemandeOr(null);
          }}
        />
      )}

        <div className="kb-piste">
          {colonnes.map(s => (
            <Colonne
              key={s.id}
              statut={s}
              dossiers={parStatut.get(s.id) || []}
              filtre={nbFiltres > 0}
              montrerMeca={!fMeca}
              onOuvrir={onOpenIntervention}
              enDeplacement={!!actif}
              factureToutes={factureToutes}
            />
          ))}
        </div>
        <DragOverlay dropAnimation={null}>
          {actif && (
            <div className="kb-carte kb-fantome">
              <span className="kb-client">{actif.client || actif.immatriculation || 'Client inconnu'}</span>
              <span className="kb-ligne2">
                <span className="kb-or">{actif.numero_or || actif.immatriculation || '—'}</span>
              </span>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
